import React, { useEffect, useState } from 'react';
import { Row, Col, Button, message, Space, Popconfirm } from 'antd';
import ListCard from 'Molecules/ListCard';
import HeadingChip from 'Molecules/HeadingChip';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getMerchantList } from '../ducks/actions'
import { removeMerchant, bulkRemoveMerchants } from '../ducks/services'
import ActionButton from 'Molecules/ActionButton';
import Search from './components/Search';
import AllRoles from '../../../../routing/config/AllRoles';
import { allowed } from '../../../../routing/config/utils';
import { getListQueryItems } from 'Modules/Application/ducks/actions';
import { useTranslate } from 'Translate';

export default (props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const merchants = useSelector((state) => state.systems.merchantList);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchVal, setSearchVal] = useState(null);
  const allowedEdit = allowed([AllRoles.SYSTEM.MERCHANT], 'write');
  const allowedDelete = allowed([AllRoles.SYSTEM.MERCHANT], 'delete');
  const globalListQuery = useSelector((state) => state.global.listQueryItems);
  const i18n = useTranslate();
  const { t } = i18n;

  const colName = [
    {
      title: t("Outlet.name"),
      dataIndex: 'store_name',
      key: 'store_name',
      className: 'enable-click',
      sorter: true
    },
    {
      title: t("System.email"),
      dataIndex: 'email',
      key: 'email',
      className: 'enable-click',
      sorter: true
    },
    {
      title: t("System.merchant_type"),
      dataIndex: 'merchant_type',
      key: 'merchant_type',
      className: 'enable-click',
      sorter: true,
      render: (text, record) => text.toUpperCase()
    },
    {
      title: t("General.status"),
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      render: (text, record) =>
        <div className='flex align-items-center'>
          <span className={text === "Active" ? "c-success" : "c-danger"}>
            {t(`General.${text?.toLowerCase()}`)}
          </span>
          {
            allowedEdit &&
            <Button type="link" onClick={() => onAction(record.id, text)}>
              {text === "Active" ? `(${t("General.deactivate")})` : `(${t("General.activate")})`}
            </Button>
          }
        </div>
    },
    
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      align: 'center',
      render: (text, record) =>
        <ActionButton
          title="merchant"
          btnAction1={() => navigate(`/merchant/${record.id}`)}
          recordId={record.id}
          onRemove={allowedDelete && onRemove}
          setLoading={props.setLoading}
          loading={props.loading}
        />
    }
  ]

  const addNew = () => navigate('/merchant/add');

  const btnList = [
    {
      title: t("Confirm.remove_select_merchants"),
      text: t("General.bulk_remove"),
      classes: 'red-btn text-white attendance-success',
      permit: true,
      action: () => onBulkRemove(),
    },
    {
      text: `+ ${t("New.merchant")}`,
      action: () => addNew(),
    },
  ];

  useEffect(() => {
    if(!globalListQuery.merchantListQuery) {
      dispatch(getMerchantList(1, limit, '', '', null, props.setLoading));
      dispatch(getListQueryItems('merchantListQuery', 1, limit, '', '', null, props.setLoading));
    } else {
      const {page, limit, sortOrder, sortColKey, searchValue} = globalListQuery.merchantListQuery
      dispatch(getMerchantList(page, limit, sortOrder, sortColKey, searchValue, props.setLoading));
      setPage(page);
      setLimit(limit);
      setSearchVal(searchValue);
    }
  }, []);

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const onAction = async (recordId, status) => {
    props.setLoading(true);

    const payload = {
      merchant_id: recordId,
      status_event: status === 'Active' ? 'deactivate' : 'activate'
    }

    await removeMerchant(payload).then(res => {
      props.setLoading(false);
      if(res.data.code === 200){
        message.success(t("Success.update_merchant"));
        setTimeout(() => dispatch(getMerchantList(page, limit, '', '', searchVal, props.setLoading)), 500);
        dispatch(getListQueryItems('merchantListQuery', page, limit, '', '', searchVal, props.setLoading))
      }
    }).catch(e => {
      props.setLoading(false);
      message.error(e.message ?? t("General.something_went_wrong"));
    })
  }

  const onRemove = async (id) => {
    props.setLoading(true);

    const payload = {
      merchant_id: id,
      status_event: 'remove'
    }

    await removeMerchant(payload).then(res => {
      props.setLoading(false);
      if(res.data.code === 200){
        message.success(t("Success.remove_merchant"));
        setTimeout(() => dispatch(getMerchantList(page, limit, '', '', searchVal, props.setLoading)), 500);
        dispatch(getListQueryItems('merchantListQuery', page, limit, '', '', searchVal, props.setLoading))
      }
    }).catch(e => {
      props.setLoading(false);
      message.error(e.message ?? t("General.something_went_wrong"));
    })
  }

  const onBulkRemove = async () => {
    props.setLoading(true);

    const payload = {
      merchants: JSON.stringify(selectedRowKeys)
    }

    await bulkRemoveMerchants(payload).then(res => {
      props.setLoading(false);
      if(res.data.code === 200){
        rowSelection.onChange([]);
        message.success(t("Success.remove_merchants"));
        setPage(1);
        setSearchVal(null);
        setTimeout(() => dispatch(getMerchantList(1, limit, '', '', null, props.setLoading)), 500);
        dispatch(getListQueryItems('merchantListQuery', 1, limit, '', '', null, props.setLoading))
      }
    }).catch(e => {
      props.setLoading(false);
      message.error(e.message ?? t("General.something_went_wrong"));
    })
  }

  const onTableChange = (pagination, filters, sorter) => {
    setPage(pagination.current);
    setLimit(pagination.pageSize);
    if (sorter.order) {
      dispatch(getMerchantList(pagination.current, pagination.pageSize, sorter.order, sorter.columnKey, searchVal, props.setLoading));
      dispatch(getListQueryItems('merchantListQuery', pagination.current, pagination.pageSize, sorter.order, sorter.columnKey, searchVal, props.setLoading))
    } else {
      dispatch(getMerchantList(pagination.current, pagination.pageSize, '', '', searchVal, props.setLoading));
      dispatch(getListQueryItems('merchantListQuery', pagination.current, pagination.pageSize, '', '', searchVal, props.setLoading))
    }
  }

  const onSearch = (search) => {
    setPage(1);
    if (search) {
      let searching = {};
      searching = {
        store_name: search.store_name,
        email: search.email
      };
      setSearchVal(searching);
      dispatch(getMerchantList(1, limit, '', '', searching, props.setLoading));
      dispatch(getListQueryItems('merchantListQuery', 1, limit, '', '', searching, props.setLoading))
    } else {
      setSearchVal(null);
      dispatch(getMerchantList(1, limit, '', '', null, props.setLoading));
      dispatch(getListQueryItems('merchantListQuery', 1, limit, '', '', null, props.setLoading))
    }
  };

  const onClickRow = (record) => {
    return {
      onClick: (e) => {
        if (e.target.closest(".enable-click"))
        navigate(`/merchant/${record.id}`)
      },
    };
  };

  return (
    <>
      <Row gutter={[10, 10]}>
        <Col span={24}>
          <HeadingChip title={t("General.merchants")} btnList={allowedEdit ? btnList : null} btnHidden={selectedRowKeys.length <= 0} />
        </Col>
        <Col span={24} className="clickRow">
          <ListCard
            onRow={onClickRow}
            Search={Search}
            onSearch={onSearch}
            searchVal={globalListQuery.merchantListQuery?.searchValue}
            ListData={merchants?.list?.map(x => ({...x, key: x.id}))}
            ListCol={colName}
            rowSelection={allowedEdit && rowSelection}
            onChange={onTableChange}
            pagination={{
              total: merchants?.total_count,
              current: page,
              pageSize: limit
            }}
          />
        </Col>
      </Row>
    </>
  )
}